import React from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { Text } from "./Typography";

const animationTime = 0.3;

type AlertType = "info" | "success" | "warning" | "error";
type AlertProps = {
  type?: AlertType;
  children: React.ReactNode;
};

function Alert({ children, type = "info", ...props }: AlertProps) {
  return (
    <AlertElement type={type} {...props}>
      <AlertText as={typeof children === "string" ? Text : "div"}>
        {children}
      </AlertText>
    </AlertElement>
  );
}

const AlertElement = styled.div<{ type: AlertType }>(({ theme, type }) => ({
  backgroundColor: theme.colors[`${type}L`],
  borderColor: theme.colors[`${type}D`],
  "&&": { color: theme.colors.fg },
  borderRadius: theme.borderRadius.sm,
  lineHeight: 1.3,
  maxWidth: `calc(100vw - ${theme.spacing.gutter})`,
  paddingTop: theme.spacing.sm,
  paddingRight: theme.spacing.sm,
  paddingBottom: theme.spacing.sm,
  paddingLeft: theme.spacing.sm,
  transition: `${animationTime}s ease opacity, ${animationTime}s ease bottom`,
  zIndex: 1000,
  animationName: fadeIn,
  animationFillMode: "backwards",
  animationDuration: `${animationTime}s`,
  animationDelay: "0s",
  "&>*": { marginTop: 0, marginBottom: 0 },
  "& button path": {
    fill: theme.colors[`${type}D`],
  },
  "&>p": { fontWeight: "bold" },
}));

AlertElement.defaultProps = {
  "aria-live": "polite",
};

const AlertText = styled.div({
  width: "auto",
  flex: "0 1 auto",
  margin: 0,
  padding: "0 .5em",
});

const fadeIn = keyframes({
  from: {
    opacity: 0,
  },
  to: {
    opacity: 1,
  },
});

export { Alert };
